import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'react-bootstrap';
import { Eye, Pencil, Trash } from 'react-bootstrap-icons';
import { Badge } from 'react-bootstrap';
import useApi from '../hooks/useApi';
import { useDocumentContext } from '../contexts/DocumentContext';
import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';

const DocumentList = () => {
    const navigate = useNavigate();
    const api = useApi();
    const apiRef = useRef(api);
    const { documentTypes } = useDocumentContext();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedType, setSelectedType] = useState(null);

    // Keep apiRef updated with latest api instance
    useEffect(() => {
        apiRef.current = api;
    }, [api]);

    useEffect(() => {
        let isMounted = true;

        const fetchDocuments = async () => {
            setLoading(true);
            try {
                const response = await apiRef.current.get('/api/v1/documents');
                if (isMounted) {
                    setDocuments(response.data || []);
                }
            } catch (error) {
                console.error('Failed to fetch documents:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchDocuments();

        return () => {
            isMounted = false;
        };
    }, []); // Empty dependency - fetch once on mount

    const getTypeName = (typeId) => {
        const type = documentTypes.find(t => t.id === typeId);
        return type ? type.name : 'Unknown';
    };

    const getExpiryStatus = (expiryDate) => {
        if (!expiryDate) return null;

        const today = new Date();
        const expiry = new Date(expiryDate);
        const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) {
            return { variant: 'danger', text: 'Expired' };
        } else if (daysUntilExpiry <= 30) {
            return { variant: 'warning', text: `${daysUntilExpiry} days` };
        }
        return null;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const typeBodyTemplate = (rowData) => {
        return getTypeName(rowData.documentTypeId);
    };

    const expiryBodyTemplate = (rowData) => {
        const status = getExpiryStatus(rowData.expiryDate);
        const formattedDate = formatDate(rowData.expiryDate);

        return (
            <div className="d-flex align-items-center gap-2">
                <span>{formattedDate}</span>
                {status && (
                    <Badge bg={status.variant}>{status.text}</Badge>
                )}
            </div>
        );
    };

    const locationBodyTemplate = (rowData) => {
        return rowData.treeNodeName || rowData.treeNodeId || '-';
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="d-flex gap-2">
                <Button
                    variant="info"
                    size="sm"
                    title="View"
                    onClick={() => handleView(rowData)}
                >
                    <Eye />
                </Button>
                <Button
                    variant="warning"
                    size="sm"
                    title="Edit"
                    onClick={() => handleEdit(rowData)}
                >
                    <Pencil />
                </Button>
                <Button
                    variant="danger"
                    size="sm"
                    title="Delete"
                    onClick={() => handleDelete(rowData)}
                >
                    <Trash />
                </Button>
            </div>
        );
    };

    const handleView = (document) => {
        navigate(`/documents/${document.id}`);
    };

    const handleEdit = (document) => {
        console.log('Edit document:', document.id);
    };

    const handleDelete = (document) => {
        console.log('Delete document:', document.id);
    };

    const handleAddDocument = () => {
        console.log('Add new document');
    };

    const filteredDocuments = documents.filter(doc => {
        if (selectedType && doc.documentTypeId !== selectedType) {
            return false;
        }
        return true;
    });

    const typeOptions = documentTypes.map(type => ({
        label: type.name,
        value: type.id
    }));

    const header = (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
                <span className="p-input-icon-left">
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Search by title..."
                    />
                </span>
                <Dropdown
                    value={selectedType}
                    options={typeOptions}
                    onChange={(e) => setSelectedType(e.value)}
                    placeholder="Filter by type"
                    showClear
                />
            </div>
            <Button variant="primary" onClick={handleAddDocument}>
                Add Document
            </Button>
        </div>
    );

    if (loading && documents.length === 0) {
        return <div>Loading documents...</div>;
    }

    return (
        <div className="document-list">
            <h2>Documents</h2>
            <DataTable
                value={filteredDocuments}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                dataKey="id"
                loading={loading}
                globalFilter={globalFilter}
                header={header}
                emptyMessage="No documents found."
                sortMode="multiple"
                removableSort
                tableStyle={{ minWidth: '50rem' }}
            >
                <Column
                    field="title"
                    header="Title"
                    sortable
                    filter
                    filterPlaceholder="Search title"
                />
                <Column
                    field="documentTypeId"
                    header="Type"
                    body={typeBodyTemplate}
                    sortable
                />
                <Column
                    field="expiryDate"
                    header="Expiry Date"
                    body={expiryBodyTemplate}
                    sortable
                />
                <Column
                    field="treeNodeId"
                    header="Location"
                    body={locationBodyTemplate}
                    sortable
                />
                <Column
                    body={actionBodyTemplate}
                    header="Actions"
                    exportable={false}
                />
            </DataTable>
        </div>
    );
};

export default DocumentList;
